import { gql, useQuery } from '@apollo/client';
import curry from 'lodash/fp/curry';
import { client } from '..';
import {
  SearchImageSourceCategoriesQuery,
  SearchImageSourceCategoriesQueryVariables,
  SearchImageSourcesQuery,
  SearchImageSourcesQueryVariables,
} from '../../__generated__/queries-web';

const GET_IMAGE_SOURCES_CATEGORIES = gql`
  query GetImageSourceCategories {
    imageSourceCategories(first: 100, orderBy: title_ASC) {
      id
      title
      sources(first: 100, orderBy: title_ASC) {
        id
        title
      }
    }
  }
`;

const SEARCH_IMAGE_SOURCES = gql`
  query SearchImageSources($where: ImageSourceFilterInput) {
    imageSources(orderBy: title_ASC, first: 100, where: $where) {
      id
      title
      category {
        id
        title
      }
      __typename
    }
  }
`;

export const searchImageSources = async (search: string) => {
  // can't use hook, used outside components
  const { data } = await client.query<
    SearchImageSourcesQuery,
    SearchImageSourcesQueryVariables
  >({
    query: SEARCH_IMAGE_SOURCES,
    variables: {
      ...(search
        ? {
            where: {
              title_contains: search,
            },
          }
        : {}),
    },
  });
  return data?.imageSources;
};

const SEARCH_IMAGE_SOURCES_CATEGORIES = gql`
  query SearchImageSourceCategories($where: ImageSourceCategoryFilterInput) {
    imageSourceCategories(first: 100, where: $where) {
      id
      title
      sources(first: 100, orderBy: title_ASC) {
        id
        title
      }
    }
  }
`;

export const useGetImageSourceCategories = () =>
  useQuery(GET_IMAGE_SOURCES_CATEGORIES);

export const searchImageSourceCategories = async (search: string) => {
  // can't use hook, used outside components
  const { data } = await client.query<
    SearchImageSourceCategoriesQuery,
    SearchImageSourceCategoriesQueryVariables
  >({
    query: SEARCH_IMAGE_SOURCES_CATEGORIES,
    variables: {
      where: {
        ...(search && {
          sources_some: {
            title_contains: search,
          },
        }),
      },
    },
  });
  return data?.imageSourceCategories;
};

// Utils function changing the result of GET_IMAGE_SOURCES_CATEGORIES
// Into a SuggestItemType array
export const mapImageSourceCategoriesToSuggestItems = (
  imageSourceCategories: any,
): any[] => {
  const addLevelToItem = curry(
    (level: number, item: { id: string; title: string }) => ({
      id: item.id,
      title: item.title,
      level,
    }),
  );
  const mapCategory = addLevelToItem(0);
  const mapSource = addLevelToItem(2);
  const result = [];

  // eslint-disable-next-line fp/no-loops
  for (const category of imageSourceCategories) {
    // eslint-disable-next-line fp/no-mutating-methods
    result.push(mapCategory(category));
    // eslint-disable-next-line fp/no-loops
    for (const source of category.sources) {
      // eslint-disable-next-line fp/no-mutating-methods
      result.push(mapSource(source));
    }
  }

  return result;
};
